import React, { type FC, type ReactElement, useEffect, useState } from "react";
import {
  LuCheck,
  LuChevronRight,
  LuCircleAlert,
  LuHeadset,
  LuShieldCheck,
  LuTimer,
  LuTriangleAlert,
  LuWifiOff,
} from "react-icons/lu";
import { useI18next } from "gatsby-plugin-react-i18next";

import { cn } from "@utilities/helper";
import {
  claimCompensation,
  escalateToManagement,
  fetchTicketState,
  formatBne,
  PtsError,
  type TicketState,
} from "@utilities/ticket-compensation";

/**
 * The wait-compensation and management-escalation banners at the top of a
 * support thread (Figma "Live Chat – Wait Compensation & Management
 * Escalation"; pts spec SPEC_CustomerCare_Escalation.md).
 *
 * - After 24 h without resolution pts offers BNE: a green banner, a claim
 *   sheet, then Adding → Added. The amount comes from pts (15 % of the last
 *   purchase), never a fixed figure.
 * - After 48 h the yellow banner replaces it: an escalation sheet that also
 *   offers the claim while the compensation is still unclaimed, then
 *   Escalating → Escalated (pts emails support management).
 * - Both actions happen once per ticket; a repeat answers "already" and the
 *   banner simply goes.
 *
 * The state is read when the thread opens and when the tab comes back into
 * focus. Sheets render inside the chat panel: a bottom sheet on phones, a
 * centred card on tablet and desktop (the panel is a corner popup there).
 *
 * Ben's confirmation bubbles arrive through the Customer Care thread once the
 * chat tells the bot (Omar's endpoint, pending): `onDone` lets the thread
 * poll for them.
 */

type Sheet =
  | { name: "claim" }
  | { name: "claiming" }
  | { name: "claimed"; amount: string }
  | { name: "claim_failed"; offline: boolean }
  | { name: "escalate" }
  | { name: "escalating" }
  | { name: "escalated" }
  | { name: "escalate_failed"; offline: boolean };

const isOffline = (): boolean =>
  typeof navigator !== "undefined" && !navigator.onLine;

const BUTTON_PRIMARY =
  "flex h-12 w-full items-center justify-center rounded-full bg-gradient-to-br from-[#F97C50] to-[#FCBC65] px-4 text-sm font-semibold text-white transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-200 active:scale-[0.98] disabled:opacity-60";
const BUTTON_SECONDARY =
  "flex h-12 w-full items-center justify-center rounded-full border border-primary bg-white px-4 text-sm font-semibold text-primary transition hover:bg-primary-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-200 active:scale-[0.98] disabled:opacity-60";

const ChatTicketBanner: FC<{
  conversationId: string;
  /** A claim or escalation went through: the thread polls for Ben's reply. */
  onDone?: (event: "compensation_claimed" | "case_escalated") => void;
}> = ({ conversationId, onDone }) => {
  const { t } = useI18next();
  const [state, setState] = useState<TicketState | null>(null);
  const [sheet, setSheet] = useState<Sheet | null>(null);

  const refresh = async (): Promise<void> => {
    try {
      setState(await fetchTicketState(conversationId));
    } catch (error) {
      // No banner is the safe default: a read failure must not block the chat.
      console.error("[ticket banner] state failed:", error);
    }
  };

  useEffect(() => {
    setState(null);
    setSheet(null);
    void refresh();
    // Coming back to the tab fires both events: one read per return is enough.
    let lastRefresh = Date.now();
    const onFocus = (): void => {
      if (document.visibilityState !== "visible") return;
      if (Date.now() - lastRefresh < 2000) return;
      lastRefresh = Date.now();
      void refresh();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [conversationId]);

  // Escape closes the sheet, except while a claim or escalation is in flight.
  // Capture phase + stopPropagation: the widget's own Escape handler (React,
  // on the panel) would otherwise close the whole chat on the same key press.
  const sheetOpen = sheet !== null;
  const sheetBusy = sheet?.name === "claiming" || sheet?.name === "escalating";
  useEffect(() => {
    if (!sheetOpen || sheetBusy) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      setSheet(null);
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [sheetOpen, sheetBusy]);

  if (state === null) return null;

  const compensation = state.compensation;
  const escalation = state.escalation;
  const canClaim =
    compensation.status === "available" &&
    compensation.claim_token !== undefined;
  const canEscalate = escalation.status === "available";
  const amount = formatBne(compensation.amount);

  const claim = async (): Promise<void> => {
    if (!canClaim || compensation.claim_token === undefined) return;
    if (isOffline()) {
      setSheet({ name: "claim_failed", offline: true });
      return;
    }
    setSheet({ name: "claiming" });
    try {
      const { result, amount: paid } = await claimCompensation(
        compensation.claim_token,
      );
      setState({
        ...state,
        compensation: {
          ...compensation,
          status: "claimed",
          claim_token: undefined,
          claim_url: undefined,
        },
      });
      if (result === "done") {
        setSheet({
          name: "claimed",
          amount: formatBne(paid ?? compensation.amount),
        });
        onDone?.("compensation_claimed");
      } else {
        // Claimed earlier through the notification link: nothing to celebrate.
        setSheet(null);
      }
    } catch (error) {
      console.error("[ticket banner] claim failed:", error);
      if (
        error instanceof PtsError &&
        (error.code === 403 || error.code === 410)
      ) {
        // Not claimable any more: drop the banner rather than invite retries.
        setState({
          ...state,
          compensation: {
            ...compensation,
            status: "not_available",
            claim_token: undefined,
          },
        });
        setSheet(null);
        return;
      }
      setSheet({ name: "claim_failed", offline: isOffline() });
    }
  };

  const escalate = async (): Promise<void> => {
    if (!canEscalate) return;
    if (isOffline()) {
      setSheet({ name: "escalate_failed", offline: true });
      return;
    }
    setSheet({ name: "escalating" });
    try {
      const { result, state: fresh } =
        await escalateToManagement(conversationId);
      setState(fresh);
      if (result === "done") {
        setSheet({ name: "escalated" });
        onDone?.("case_escalated");
      } else {
        setSheet(null);
      }
    } catch (error) {
      console.error("[ticket banner] escalate failed:", error);
      if (
        error instanceof PtsError &&
        (error.code === 404 || error.code === 410)
      ) {
        setState({
          ...state,
          escalation: { ...escalation, status: "not_available" },
        });
        setSheet(null);
        return;
      }
      setSheet({ name: "escalate_failed", offline: isOffline() });
    }
  };

  const close = (): void => {
    setSheet(null);
  };

  const banner = canEscalate ? (
    <Banner
      tone="warning"
      icon={<LuTriangleAlert aria-hidden="true" className="size-4 shrink-0" />}
      text={t("chat_esc_banner")}
      action={t("chat_comp_view")}
      onClick={() => {
        setSheet({ name: "escalate" });
      }}
    />
  ) : canClaim ? (
    <Banner
      tone="success"
      icon={<LuTimer aria-hidden="true" className="size-4 shrink-0" />}
      text={t("chat_comp_banner", { amount })}
      action={t("chat_comp_view")}
      onClick={() => {
        setSheet({ name: "claim" });
      }}
    />
  ) : null;

  const sheetContent = (): ReactElement | null => {
    switch (sheet?.name) {
      case "claim":
        return (
          <SheetBody
            icon={<CoinIcon />}
            title={t("chat_comp_title", { amount })}
            text={t("chat_comp_text", { amount })}
            primary={{
              label: t("chat_comp_claim", { amount }),
              onClick: claim,
            }}
            secondary={{ label: t("chat_comp_not_now"), onClick: close }}
          />
        );
      case "claiming":
        return (
          <SheetBody
            icon={<Spinner />}
            title={t("chat_comp_adding_title", { amount })}
            text={t("chat_comp_adding_text")}
          />
        );
      case "claimed":
        return (
          <SheetBody
            icon={<SuccessIcon />}
            title={t("chat_comp_added_title", { amount: sheet.amount })}
            text={t("chat_comp_added_text")}
            primary={{ label: t("chat_comp_done"), onClick: close }}
          />
        );
      case "claim_failed":
        return (
          <SheetBody
            icon={sheet.offline ? <OfflineIcon /> : <ErrorIcon />}
            title={
              sheet.offline
                ? t("chat_offline_title")
                : t("chat_comp_failed_title")
            }
            text={
              sheet.offline
                ? t("chat_offline_text")
                : t("chat_comp_failed_text")
            }
            note={{
              icon: (
                <LuShieldCheck
                  aria-hidden="true"
                  className="size-4 shrink-0 text-emerald-600"
                />
              ),
              text: t("chat_comp_saved", { amount }),
            }}
            primary={{ label: t("chat_retry"), onClick: claim }}
            secondary={{ label: t("chat_comp_not_now"), onClick: close }}
          />
        );
      case "escalate":
        return (
          <SheetBody
            icon={
              <LuTriangleAlert
                aria-hidden="true"
                className="size-14 text-red-600"
              />
            }
            title={t("chat_esc_title")}
            text={t("chat_esc_text")}
            note={
              canClaim
                ? {
                    icon: (
                      <LuTimer
                        aria-hidden="true"
                        className="size-4 shrink-0 text-amber-700"
                      />
                    ),
                    text: t("chat_esc_note", { amount }),
                    tone: "warning",
                  }
                : undefined
            }
            primary={{ label: t("chat_esc_button"), onClick: escalate }}
            secondary={
              canClaim
                ? { label: t("chat_esc_claim"), onClick: claim }
                : { label: t("chat_comp_not_now"), onClick: close }
            }
          />
        );
      case "escalating":
        return (
          <SheetBody
            icon={<Spinner />}
            title={t("chat_esc_sending_title")}
            text={t("chat_esc_sending_text")}
          />
        );
      case "escalated":
        return (
          <SheetBody
            icon={<SuccessIcon />}
            title={t("chat_esc_done_title")}
            text={t("chat_esc_done_text")}
            primary={{ label: t("chat_comp_done"), onClick: close }}
          />
        );
      case "escalate_failed":
        return (
          <SheetBody
            icon={sheet.offline ? <OfflineIcon /> : <ErrorIcon />}
            title={
              sheet.offline
                ? t("chat_offline_title")
                : t("chat_esc_failed_title")
            }
            text={
              sheet.offline
                ? t("chat_offline_text")
                : t("chat_comp_failed_text")
            }
            note={{
              icon: (
                <LuHeadset
                  aria-hidden="true"
                  className="size-4 shrink-0 text-emerald-600"
                />
              ),
              text: t("chat_esc_still_open"),
            }}
            primary={{ label: t("chat_retry"), onClick: escalate }}
            secondary={{ label: t("chat_comp_not_now"), onClick: close }}
          />
        );
      default:
        return null;
    }
  };

  const content = sheetContent();

  return (
    <>
      {banner}
      {content !== null && (
        <div
          className="absolute inset-0 z-20 flex items-end justify-center bg-black/40 md:items-center md:p-6"
          onClick={() => {
            if (!sheetBusy) close();
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-busy={sheetBusy}
            onClick={event => {
              event.stopPropagation();
            }}
            className="chat-in w-full rounded-t-3xl bg-white px-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-3 md:max-w-sm md:rounded-3xl md:p-6"
          >
            <span
              aria-hidden="true"
              className="mx-auto mb-5 block h-1 w-9 rounded-full bg-black/10 md:hidden"
            />
            {content}
          </div>
        </div>
      )}
    </>
  );
};

const Banner: FC<{
  tone: "success" | "warning";
  icon: ReactElement;
  text: string;
  action: string;
  onClick: () => void;
}> = ({ tone, icon, text, action, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "chat-in flex h-11 w-full shrink-0 items-center gap-2 px-4 text-start text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-200",
      tone === "success"
        ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200/70"
        : "bg-amber-100 text-amber-700 hover:bg-amber-200/70",
    )}
  >
    {icon}
    <span className="min-w-px flex-1 truncate">{text}</span>
    <span className="flex shrink-0 items-center gap-0.5">
      {action}
      <LuChevronRight aria-hidden="true" className="size-4 rtl:-scale-x-100" />
    </span>
  </button>
);

const SheetBody: FC<{
  icon: ReactElement;
  title: string;
  text: string;
  note?: { icon: ReactElement; text: string; tone?: "warning" };
  primary?: { label: string; onClick: () => void | Promise<void> };
  secondary?: { label: string; onClick: () => void | Promise<void> };
}> = ({ icon, title, text, note, primary, secondary }) => (
  <div className="flex flex-col items-center gap-3 text-center">
    <div className="flex h-16 items-center justify-center">{icon}</div>
    <p className="mb-0 text-lg font-bold text-black">{title}</p>
    <p className="mb-0 text-sm text-black/70">{text}</p>
    {note !== undefined && (
      <p
        className={cn(
          "mb-0 flex w-full items-center gap-2 rounded-2xl px-4 py-3 text-start text-sm font-semibold",
          note.tone === "warning"
            ? "bg-amber-50 text-black"
            : "bg-black/5 text-black",
        )}
      >
        {note.icon}
        <span className="min-w-px flex-1">{note.text}</span>
      </p>
    )}
    {(primary !== undefined || secondary !== undefined) && (
      <div className="mt-2 flex w-full flex-col gap-2.5">
        {primary !== undefined && (
          <button
            type="button"
            className={BUTTON_PRIMARY}
            onClick={() => {
              void primary.onClick();
            }}
          >
            {primary.label}
          </button>
        )}
        {secondary !== undefined && (
          <button
            type="button"
            className={BUTTON_SECONDARY}
            onClick={() => {
              void secondary.onClick();
            }}
          >
            {secondary.label}
          </button>
        )}
      </div>
    )}
  </div>
);

const CoinIcon: FC = () => (
  <span
    aria-hidden="true"
    className="flex size-16 items-center justify-center rounded-full bg-tertiary text-2xl font-black text-white shadow-inner"
  >
    B
  </span>
);

const SuccessIcon: FC = () => (
  <span
    aria-hidden="true"
    className="flex size-14 items-center justify-center rounded-full bg-emerald-600 text-white"
  >
    <LuCheck className="size-8" strokeWidth={3} />
  </span>
);

const ErrorIcon: FC = () => (
  <span
    aria-hidden="true"
    className="flex size-14 items-center justify-center rounded-full bg-red-50 text-red-600"
  >
    <LuCircleAlert className="size-8" />
  </span>
);

const OfflineIcon: FC = () => (
  <span
    aria-hidden="true"
    className="flex size-14 items-center justify-center rounded-full bg-black/5 text-black/70"
  >
    <LuWifiOff className="size-7" />
  </span>
);

const Spinner: FC = () => (
  <span
    aria-hidden="true"
    className="size-4 animate-pulse rounded-full bg-primary"
  />
);

export default ChatTicketBanner;
